/** Number of calendar months loaded per availability API call. */
export const MARKETPLACE_AVAILABILITY_CHUNK_MONTHS = 3;

export function formatMarketplaceDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function getMonthKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}`;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function monthDiff(from: Date, to: Date): number {
  return (
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth())
  );
}

/** 1st of the 3-month chunk containing `month`, aligned to `anchorMonth`. */
export function getChunkStartMonth(anchorMonth: Date, month: Date): Date {
  const anchor = startOfMonth(anchorMonth);
  const target = startOfMonth(month);
  const diff = monthDiff(anchor, target);
  const chunkIndex = Math.floor(diff / MARKETPLACE_AVAILABILITY_CHUNK_MONTHS);
  return addMonths(anchor, chunkIndex * MARKETPLACE_AVAILABILITY_CHUNK_MONTHS);
}

export function getThreeMonthChunkRange(chunkStart: Date): {
  start: string;
  end: string;
} {
  const start = startOfMonth(chunkStart);
  const end = new Date(
    start.getFullYear(),
    start.getMonth() + MARKETPLACE_AVAILABILITY_CHUNK_MONTHS,
    0
  );
  return {
    start: formatMarketplaceDateKey(start),
    end: formatMarketplaceDateKey(end),
  };
}

export function getMonthsInChunk(chunkStart: Date): string[] {
  const start = startOfMonth(chunkStart);
  return Array.from(
    { length: MARKETPLACE_AVAILABILITY_CHUNK_MONTHS },
    (_, i) => getMonthKey(addMonths(start, i))
  );
}

export function getMonthIndexInChunk(
  chunkStart: Date,
  month: Date
): number {
  return monthDiff(startOfMonth(chunkStart), startOfMonth(month));
}

export function getMonthKeysInRange(start: string, end: string): string[] {
  const keys = new Set<string>();
  const current = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);

  while (current <= endDate) {
    keys.add(getMonthKey(current));
    current.setMonth(current.getMonth() + 1);
  }

  return Array.from(keys);
}

/** SSR fallback when the first 3-month chunk has no availability — next 3-month window. */
export function getMarketplaceAvailabilityFallbackRange(today: Date): {
  start: string;
  end: string;
} {
  const nextChunkStart = addMonths(startOfMonth(today), MARKETPLACE_AVAILABILITY_CHUNK_MONTHS);
  return getThreeMonthChunkRange(nextChunkStart);
}
